import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import useForm from '@afosto/utils-react/validation/useForm';
import CurrencyField from '@afosto/app-components-react/CurrencyField';
import Form from '@afosto/app-components-react/Form';
import Icon from '@afosto/app-components-react/Icon';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';
import PercentageField from '@afosto/app-components-react/PercentageField';
import TextField from '@afosto/app-components-react/TextField';
import ToggleButtonGroup from '@afosto/app-components-react/ToggleButtonGroup';
import validationSchema from './validations';
import translations from './translations';

const DiscountForm = ({
  discount,
  onSubmit,
  onCancel,
  onChange,
  onEdit,
  readOnly,
  renderActions,
  inline,
  isEditing,
}) => {
  const intl = useIntl();
  const form = useForm({
    validationSchema,
    isEditing,
    editInline: inline,
    defaultValues: {
      description: discount?.description,
      type: discount?.type ?? 'amount',
      amount: discount?.amount ?? '',
    },
    onSubmit,
    onCancel,
  });
  const { setEditInline, setIsEditing, setValue, watch } = form;
  const typeValue = watch('type');

  const handleOnChange = name => (event, newValue) => {
    setValue(name, newValue);

    if (onChange) {
      onChange({ ...discount, ...form.getValues(), [name]: newValue });
    }
  };

  useEffect(() => {
    setEditInline(inline);
  }, [inline, setEditInline]);

  useEffect(() => {
    setIsEditing(isEditing);
  }, [isEditing, setIsEditing]);

  /**
   * Handle changing the amount type.
   * @param {Event} event
   * @param {string} newType
   */
  const handleTypeChange = (event, newType) => {
    setValue('amount', '');
    handleOnChange('type')(event, newType);
  };

  return (
    <LoadingWrapper isLoading={form.isSubmitting}>
      <Form form={form} renderActions={renderActions} component="div">
        <TextField
          name="description"
          label={intl.formatMessage(translations.fields.description.label)}
          onEdit={onEdit}
          onChange={handleOnChange('description')}
          readOnly={readOnly}
        />

        <ToggleButtonGroup
          name="type"
          margin="none"
          onChange={handleTypeChange}
          options={[
            {
              label: <Icon icon={['fal', 'euro-sign']} size="small" />,
              value: 'amount',
            },
            {
              label: <Icon icon={['fal', 'percentage']} size="small" />,
              value: 'percentage',
            },
          ]}
          readOnly={readOnly}
        />

        {typeValue === 'percentage' && (
          <PercentageField
            name="amount"
            label={intl.formatMessage(translations.fields.percentage.label)}
            onEdit={onEdit}
            onChange={handleOnChange('amount')}
            readOnly={readOnly}
          />
        )}

        {typeValue === 'amount' && (
          <CurrencyField
            name="amount"
            label={intl.formatMessage(translations.fields.amount.label)}
            onEdit={onEdit}
            onChange={handleOnChange('amount')}
            readOnly={readOnly}
          />
        )}
      </Form>
    </LoadingWrapper>
  );
};

DiscountForm.propTypes = {
  discount: PropTypes.object,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  onChange: PropTypes.func,
  onEdit: PropTypes.func,
  readOnly: PropTypes.bool,
  renderActions: PropTypes.bool,
  inline: PropTypes.bool,
  isEditing: PropTypes.bool,
};

DiscountForm.defaultProps = {
  discount: {},
  onSubmit: undefined,
  onCancel: undefined,
  onChange: undefined,
  onEdit: undefined,
  readOnly: false,
  renderActions: true,
  inline: false,
  isEditing: true,
};

export default DiscountForm;
